import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Link as MuiLink,
  Stack,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  Cookie as CookieIcon,
} from '@mui/icons-material';
// Removed useNavigate - will use window.location instead

const COOKIE_CONSENT_KEY = 'naewaste_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'naewaste_cookie_preferences';

// Cookie categories
const COOKIE_CATEGORIES = {
  essential: {
    name: 'Essential Cookies',
    description: 'These cookies are necessary for the website to function properly. They include authentication and security features. These cannot be disabled.',
    required: true,
  },
  analytics: {
    name: 'Analytics Cookies',
    description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
    required: false,
  },
  preferences: {
    name: 'Preference Cookies',
    description: 'These cookies remember your preferences and settings to provide a personalized experience.',
    required: false,
  },
};

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    preferences: false,
  });
  // Removed navigate - using window.location for navigation

  const handleOpenSettings = React.useCallback(() => {
    // Load current preferences
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        setPreferences({
          essential: true,
          analytics: parsed.analytics || false,
          preferences: parsed.preferences || false,
        });
      } catch (e) {
        console.error('Error parsing cookie preferences:', e);
      }
    }
    setShowDetails(true);
    setOpen(true);
  }, []);

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);

    if (!consent) {
      // Show banner if no consent has been given
      setOpen(true);
    } else if (savedPreferences) {
      // Load saved preferences
      try {
        const parsed = JSON.parse(savedPreferences);
        setPreferences({
          essential: true, // Always true
          analytics: parsed.analytics || false,
          preferences: parsed.preferences || false,
        });
      } catch (e) {
        console.error('Error parsing cookie preferences:', e);
      }
    }

    // Listen for custom event to open cookie settings
    window.addEventListener('openCookieSettings', handleOpenSettings);
    return () => {
      window.removeEventListener('openCookieSettings', handleOpenSettings);
    };
  }, [handleOpenSettings]);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      preferences: true,
    };
    saveConsent(allAccepted);
    setOpen(false);
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      preferences: false,
    };
    saveConsent(onlyEssential);
    setOpen(false);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    setOpen(false);
  };

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    localStorage.setItem(`${COOKIE_CONSENT_KEY}_date`, new Date().toISOString());
    
    // Apply cookie preferences (you can extend this to actually control cookie usage)
    console.log('Cookie preferences saved:', prefs);
  };

  const handlePreferenceChange = (category) => (event) => {
    if (category === 'essential') {
      // Essential cookies cannot be disabled
      return;
    }
    setPreferences((prev) => ({
      ...prev,
      [category]: event.target.checked,
    }));
  };

  // Don't render if consent has been given and banner is closed
  if (!open && localStorage.getItem(COOKIE_CONSENT_KEY)) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {}} // Prevent closing by clicking outside
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            position: 'fixed',
            bottom: showDetails ? 'auto' : 20,
            right: showDetails ? 'auto' : 20,
            m: showDetails ? 2 : 0,
            maxHeight: showDetails ? '90vh' : 'auto',
          },
        }}
      >
        {!showDetails ? (
          // Simple banner view
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CookieIcon color="primary" />
                <Typography variant="h6" component="span">
                  Cookie Consent
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" paragraph>
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                By clicking &quot;Accept All&quot;, you consent to our use of cookies.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <MuiLink
                  component="button"
                  variant="body2"
                  onClick={() => setShowDetails(true)}
                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Learn more about our cookies
                </MuiLink>
                {' or '}
                <MuiLink
                  component="button"
                  variant="body2"
                  onClick={() => { window.location.href = '/privacy'; }}
                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                >
                  view our Privacy Policy
                </MuiLink>
                .
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Button
                onClick={handleRejectAll}
                variant="outlined"
                size="small"
                sx={{ minWidth: 'auto' }}
              >
                Reject All
              </Button>
              <Button
                onClick={() => setShowDetails(true)}
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon />}
                sx={{ minWidth: 'auto' }}
              >
                Customize
              </Button>
              <Button
                onClick={handleAcceptAll}
                variant="contained"
                size="small"
                sx={{ minWidth: 'auto' }}
              >
                Accept All
              </Button>
            </DialogActions>
          </>
        ) : (
          // Detailed preferences view
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CookieIcon color="primary" />
                  <Typography variant="h6">Cookie Preferences</Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setShowDetails(false);
                    if (localStorage.getItem(COOKIE_CONSENT_KEY)) {
                      setOpen(false);
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" paragraph>
                You can choose which types of cookies to accept. Essential cookies are required for the website to function and cannot be disabled.
              </Typography>

              <Stack spacing={3} sx={{ mt: 2 }}>
                {Object.entries(COOKIE_CATEGORIES).map(([key, category]) => (
                  <Box key={key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={preferences[key]}
                          onChange={handlePreferenceChange(key)}
                          disabled={category.required}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {category.name}
                            {category.required && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                (Required)
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {category.description}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary">
                For more information, please read our{' '}
                <MuiLink
                  component="button"
                  variant="body2"
                  onClick={() => {
                    setOpen(false);
                    window.location.href = '/privacy';
                  }}
                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Privacy Policy
                </MuiLink>
                .
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleRejectAll} variant="outlined" size="small">
                Reject All
              </Button>
              <Button
                onClick={handleSavePreferences}
                variant="contained"
                size="small"
              >
                Save Preferences
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Cookie Settings Button - shown when consent is given */}
      {localStorage.getItem(COOKIE_CONSENT_KEY) && !open && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={<CookieIcon />}
            onClick={handleOpenSettings}
            sx={{
              borderRadius: '20px',
              boxShadow: 3,
            }}
          >
            Cookie Settings
          </Button>
        </Box>
      )}
    </>
  );
}

// Export function to open cookie settings from anywhere
export const openCookieSettings = () => {
  // This can be called from other components
  const event = new CustomEvent('openCookieSettings');
  window.dispatchEvent(event);
};

